import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    fetchBrands, adminUpdateBrand, adminDeleteBrand,
    adminAddItem, adminUpdateItem, adminDeleteItem,
    adminBulkAddItems, adminExtractMenu, adminAutoCategorise, adminAllergens,
    adminImportMenuFromUrl, adminImportMenuCommit,
    adminReportJson, adminReportPdfBlob, adminSendReport,
    adminPayoutReportJson, adminPayoutReportPdfBlob,
} from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { AdminShell, Modal } from "./AdminShell";
import { formatMoney } from "@/lib/orderUtils";
import { groupItemsByCategory, UNCATEGORISED_LABEL } from "@/lib/menuGrouping";
import { SortableMenuList } from "@/components/SortableMenuList";
import { KioskDealsModal } from "@/components/KioskDealsModal";
import { QRCodeModal } from "@/components/QRCodeModal";
import { ImageUploadField } from "@/components/ImageUploadField";
import { SavedOptionsPicker, SaveGroupControl, SavedOptionsModal } from "@/components/SavedOptions";

const ITEM_TAG_OPTIONS = [
    { value: "best_seller", label: "Best seller", icon: "★" },
    { value: "halal", label: "Halal", icon: "☪" },
    { value: "vegan", label: "Vegan", icon: "🌱" },
    { value: "veggie", label: "Veggie", icon: "🥬" },
    { value: "spicy", label: "Spicy", icon: "🌶" },
];


const BADGE_TYPES = [
    { value: "pct_off",       label: "Percentage off",    needsValue: true,  unit: "%"  },
    { value: "amount_off",    label: "Amount off (£)",    needsValue: true,  unit: "£"  },
    { value: "free_delivery", label: "Free delivery",     needsValue: false, unit: ""   },
    { value: "marketing",     label: "Marketing badge",   needsValue: false, unit: ""   },
];

const DAYS = [
    { v: 0, l: "Mon" }, { v: 1, l: "Tue" }, { v: 2, l: "Wed" },
    { v: 3, l: "Thu" }, { v: 4, l: "Fri" }, { v: 5, l: "Sat" }, { v: 6, l: "Sun" },
];

/** ISO datetime <-> <input type="datetime-local"> string. The input is naive
 * local time; we treat that as Europe/London (kitchen's TZ) and store as UTC. */
const isoToLocalInput = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const parts = new Intl.DateTimeFormat("en-GB", {
            timeZone: "Europe/London",
            year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit", hour12: false,
        }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
        return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
    } catch { return ""; }
};
const localInputToIso = (val) => {
    if (!val) return null;
    try {
        // The wallclock time the user picked, in Europe/London.
        // Determine London's UTC offset at that instant by formatting a known UTC
        // timestamp and reading the resulting wallclock back.
        const asLocal = new Date(val + ":00");          // browser-local interpretation
        const asUTC = new Date(val + ":00Z");           // UTC interpretation
        // diff between (browser-local of asUTC) and (Europe/London of asUTC)
        const londonStr = asUTC.toLocaleString("en-GB", { timeZone: "Europe/London", hour12: false });
        const utcStr    = asUTC.toLocaleString("en-GB", { timeZone: "UTC", hour12: false });
        const parse = (s) => { const [d, t] = s.split(", "); const [dd,mm,yy] = d.split("/"); return Date.parse(`${yy}-${mm}-${dd}T${t}Z`); };
        const londonOffsetMin = (parse(londonStr) - parse(utcStr)) / 60000; // +60 for BST, 0 for GMT
        return new Date(asUTC.getTime() - londonOffsetMin * 60000).toISOString();
    } catch { return null; }
};

const BadgeScheduleEditor = ({ badge, idx, onUpdate }) => {
    const hasSchedule = !!(badge.starts_at || badge.ends_at || (badge.days_of_week || []).length || badge.start_hour != null || badge.end_hour != null);
    const [open, setOpen] = useState(hasSchedule);
    const days = badge.days_of_week || [];
    const toggleDay = (d) => {
        const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort();
        onUpdate(idx, { days_of_week: next });
    };
    const clearSchedule = () => onUpdate(idx, {
        starts_at: null, ends_at: null, days_of_week: [], start_hour: null, end_hour: null,
    });

    return (
        <div className="mt-3 pt-3 border-t border-[var(--border)]" data-testid={`brand-badge-schedule-${idx}`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-[var(--text)] flex items-center gap-2"
                data-testid={`brand-badge-schedule-toggle-${idx}`}
            >
                <span>⏰ Schedule {hasSchedule ? "(active)" : "(optional — empty = always on)"}</span>
                <span>{open ? "▾" : "▸"}</span>
            </button>
            {open && (
                <div className="mt-3 space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1">Starts (UK time, optional)</label>
                            <input
                                type="datetime-local"
                                className="input"
                                value={isoToLocalInput(badge.starts_at)}
                                onChange={(e) => onUpdate(idx, { starts_at: localInputToIso(e.target.value) })}
                                data-testid={`brand-badge-starts-${idx}`}
                            />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1">Ends (UK time, optional)</label>
                            <input
                                type="datetime-local"
                                className="input"
                                value={isoToLocalInput(badge.ends_at)}
                                onChange={(e) => onUpdate(idx, { ends_at: localInputToIso(e.target.value) })}
                                data-testid={`brand-badge-ends-${idx}`}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1.5">Days of week (none selected = every day)</label>
                        <div className="flex flex-wrap gap-1.5">
                            {DAYS.map((d) => {
                                const on = days.includes(d.v);
                                return (
                                    <button
                                        key={d.v}
                                        type="button"
                                        onClick={() => toggleDay(d.v)}
                                        className={`font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border transition ${
                                            on ? "bg-[var(--gold)] text-[#0a0a0a] border-[var(--gold)]"
                                               : "border-[var(--border)] text-muted hover:text-[var(--text)] hover:border-[var(--text)]"
                                        }`}
                                        data-testid={`brand-badge-day-${idx}-${d.v}`}
                                    >
                                        {d.l}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1">Daily start hour (0-23, UK)</label>
                            <input
                                type="number" min="0" max="23"
                                className="input"
                                value={badge.start_hour ?? ""}
                                placeholder="—"
                                onChange={(e) => onUpdate(idx, { start_hour: e.target.value === "" ? null : Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)) })}
                                data-testid={`brand-badge-start-hour-${idx}`}
                            />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1">Daily end hour (0-24, UK)</label>
                            <input
                                type="number" min="0" max="24"
                                className="input"
                                value={badge.end_hour ?? ""}
                                placeholder="—"
                                onChange={(e) => onUpdate(idx, { end_hour: e.target.value === "" ? null : Math.max(0, Math.min(24, parseInt(e.target.value, 10) || 0)) })}
                                data-testid={`brand-badge-end-hour-${idx}`}
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="font-mono text-[10px] text-muted">
                            Example: Tue/Wed at 16-19 → "Early Bird, 4-7 PM, Tue & Wed". Leave blank for always-on.
                        </p>
                        <button
                            type="button"
                            onClick={clearSchedule}
                            className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-[#ff5246]"
                            data-testid={`brand-badge-schedule-clear-${idx}`}
                        >
                            ✕ Clear schedule
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};


const BadgesEditor = ({ badges, onAdd, onUpdate, onRemove }) => (
    <section className="mt-7 pt-5 border-t border-[var(--border)]" data-testid="brand-badges-editor">
        <div className="flex items-end justify-between mb-3">
            <div>
                <p className="eyebrow mb-1">// Promo &amp; marketing badges</p>
                <p className="text-muted text-[12px] font-light max-w-xl">
                    Stacked on the brand card and the QR menu hero. Discount badges auto-apply at checkout once the customer's spend from this brand meets the minimum. Marketing badges are display-only.
                </p>
            </div>
            <button onClick={onAdd} className="btn-secondary" data-testid="brand-add-badge">+ Add badge</button>
        </div>
        {badges.length === 0 ? (
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted py-4">No badges. Tap + Add badge to create one.</p>
        ) : (
            <div className="space-y-3">
                {badges.map((b, idx) => {
                    const def = BADGE_TYPES.find((t) => t.value === b.type) || BADGE_TYPES[0];
                    return (
                        <div key={b.id || idx} className="border border-[var(--border)] p-3 bg-[var(--surface-elevated)]" data-testid={`brand-badge-row-${idx}`}>
                            <div className="grid md:grid-cols-12 gap-3 items-end">
                                <div className="md:col-span-3">
                                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1.5">Type</label>
                                    <select
                                        className="input"
                                        value={b.type}
                                        onChange={(e) => onUpdate(idx, { type: e.target.value })}
                                        data-testid={`brand-badge-type-${idx}`}
                                    >
                                        {BADGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                {def.needsValue && (
                                    <div className="md:col-span-2">
                                        <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1.5">{def.unit === "%" ? "Percent (%)" : "Amount (£)"}</label>
                                        <input
                                            type="number" step={def.unit === "%" ? "1" : "0.5"} min="0"
                                            className="input"
                                            value={b.value || 0}
                                            onChange={(e) => onUpdate(idx, { value: parseFloat(e.target.value) || 0 })}
                                            data-testid={`brand-badge-value-${idx}`}
                                        />
                                    </div>
                                )}
                                <div className={def.needsValue ? "md:col-span-2" : "md:col-span-3"}>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1.5">Min spend (£)</label>
                                    <input
                                        type="number" step="0.5" min="0"
                                        className="input"
                                        value={b.min_spend || 0}
                                        onChange={(e) => onUpdate(idx, { min_spend: parseFloat(e.target.value) || 0 })}
                                        placeholder="0 = always"
                                        data-testid={`brand-badge-min-${idx}`}
                                    />
                                </div>
                                <div className={def.needsValue ? "md:col-span-3" : "md:col-span-4"}>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1.5">Custom label (optional)</label>
                                    <input
                                        type="text"
                                        className="input"
                                        value={b.label || ""}
                                        onChange={(e) => onUpdate(idx, { label: e.target.value })}
                                        placeholder={b.type === "marketing" ? "e.g. New menu" : "e.g. 20% off your meal"}
                                        data-testid={`brand-badge-label-${idx}`}
                                    />
                                </div>
                                <div className="md:col-span-2 flex items-center justify-between gap-2 pb-0.5">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={b.active !== false}
                                            onChange={(e) => onUpdate(idx, { active: e.target.checked })}
                                            className="accent-[var(--gold)] w-4 h-4"
                                            data-testid={`brand-badge-active-${idx}`}
                                        />
                                        <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Active</span>
                                    </label>
                                    <button
                                        onClick={() => onRemove(idx)}
                                        className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-[#ff5246]"
                                        data-testid={`brand-badge-remove-${idx}`}
                                    >
                                        ✕ Remove
                                    </button>
                                </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-[var(--border)] flex flex-wrap items-center gap-2">
                                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mr-1">Applies to:</span>
                                {[
                                    { v: "both",       l: "Both" },
                                    { v: "delivery",   l: "Delivery only" },
                                    { v: "collection", l: "Collection only" },
                                ].map((opt) => {
                                    const current = (b.applies_to || "both").toLowerCase();
                                    const selected = current === opt.v;
                                    return (
                                        <button
                                            key={opt.v}
                                            type="button"
                                            onClick={() => onUpdate(idx, { applies_to: opt.v })}
                                            className={`font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border transition ${
                                                selected
                                                    ? "bg-[var(--gold)] text-[#0a0a0a] border-[var(--gold)]"
                                                    : "border-[var(--border)] text-muted hover:text-[var(--text)] hover:border-[var(--text)]"
                                            }`}
                                            data-testid={`brand-badge-applies-${idx}-${opt.v}`}
                                        >
                                            {opt.l}
                                        </button>
                                    );
                                })}
                                {b.type === "free_delivery" && (b.applies_to || "both") === "collection" && (
                                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff5246] ml-2">
                                        ⚠ "Free delivery" on collection-only never triggers
                                    </span>
                                )}
                            </div>
                            <BadgeScheduleEditor badge={b} idx={idx} onUpdate={onUpdate} />
                        </div>
                    );
                })}
            </div>
        )}
    </section>
);



const ALLERGEN_LABELS = {
    gluten: "Gluten",
    dairy: "Dairy",
    egg: "Egg",
    soy: "Soy",
    peanut: "Peanut",
    tree_nut: "Tree nut",
    sesame: "Sesame",
    fish: "Fish",
    shellfish: "Shellfish",
    celery: "Celery",
    mustard: "Mustard",
    lupin: "Lupin",
    sulphite: "Sulphite",
    mollusc: "Mollusc",
};

const AdminBrandEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAdminAuth();
    const [brand, setBrand] = useState(null);
    const [editingItem, setEditingItem] = useState(null); // item obj or "new"
    const [showImport, setShowImport] = useState(false);
    const [showSavedOptions, setShowSavedOptions] = useState(false);
    const [showKioskDeals, setShowKioskDeals] = useState(false);
    const [showQR, setShowQR] = useState(false);
    const [showReports, setShowReports] = useState(false);
    const [allergens, setAllergens] = useState([]);
    const [savingBrand, setSavingBrand] = useState(false);
    const [categorising, setCategorising] = useState(false);

    const refresh = async () => {
        const bs = await fetchBrands(false);
        const found = bs.find((b) => b.id === id);
        setBrand(found || null);
    };

    useEffect(() => {
        refresh();
        adminAllergens().then((r) => setAllergens(r.allergens)).catch(() => {});
    // eslint-disable-next-line
    }, [id]);

    if (!brand) {
        return (
            <AdminShell>
                <p className="text-muted">Loading brand…</p>
            </AdminShell>
        );
    }

    const isMaster = user?.role === "master_admin";

    const saveBrand = async (patch) => {
        setSavingBrand(true);
        try {
            const updated = await adminUpdateBrand(brand.id, patch);
            setBrand(updated);
        } finally {
            setSavingBrand(false);
        }
    };

    const deleteBrand = async () => {
        if (!window.confirm(`Delete brand "${brand.name}" and all its menu items? This cannot be undone.`)) return;
        try {
            await adminDeleteBrand(brand.id);
            navigate("/admin");
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Unknown error";
            alert(`Could not delete brand:\n\n${msg}\n\nCheck the browser console for details.`);
            console.error("Delete brand failed:", e);
        }
    };

    const autoCategorise = async (overwrite = false) => {
        const uncatCount = (brand.items || []).filter((it) => !(it.category || "").trim()).length;
        if (!overwrite && uncatCount === 0) {
            if (!window.confirm("All items already have a category. Re-run AI on every item (this will overwrite existing categories)?")) return;
            overwrite = true;
        }
        setCategorising(true);
        try {
            const r = await adminAutoCategorise(brand.id, overwrite);
            await refresh();
            alert(`✓ Categorised ${r.updated} items`);
        } catch (e) {
            alert(e.response?.data?.detail || "AI categorisation failed. Try again.");
        } finally {
            setCategorising(false);
        }
    };

    return (
        <AdminShell
            eyebrow={brand.cuisine || "Brand"}
            title={brand.name}
            right={
                <div className="flex flex-wrap items-center gap-3">
                    <button onClick={() => setShowQR(true)} className="btn-secondary" data-testid="open-qr-modal">
                        ▦ QR Code
                    </button>
                    <button onClick={() => setShowReports(true)} className="btn-secondary" data-testid="open-reports">
                        📊 Reports & payout
                    </button>
                    <button onClick={() => setShowKioskDeals(true)} className="btn-secondary" data-testid="open-kiosk-deals">
                        🖥 Kiosk deals
                    </button>
                    <button onClick={() => setShowSavedOptions(true)} className="btn-secondary" data-testid="open-saved-options">
                        🧩 Saved options
                    </button>
                    <button onClick={() => setShowImport(true)} className="btn-secondary" data-testid="open-import-pdf">
                        📄 Import menu PDF or photo
                    </button>
                    <button onClick={() => setEditingItem("new")} className="btn-primary" data-testid="add-item-btn">
                        + Add item
                    </button>
                </div>
            }
        >
            {/* Brand settings */}
            {showKioskDeals && <KioskDealsModal brand={brand} onClose={() => setShowKioskDeals(false)} onChanged={refresh} />}
            <BrandSettingsPanel
                brand={brand}
                saving={savingBrand}
                onSave={saveBrand}
                isMaster={isMaster}
                onDelete={deleteBrand}
            />

            {/* Items list */}
            <section className="mt-12">
                <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <p className="eyebrow">// Menu</p>
                        <h2 className="font-display text-[32px] mt-2">
                            {brand.items?.length || 0} dishes
                        </h2>
                    </div>
                    {brand.items?.length > 0 && (
                        <div className="flex items-center gap-3 flex-wrap">
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">
                                Reorder ▲▼ or drag ⠿
                            </span>
                            <button
                                onClick={() => autoCategorise(false)}
                                disabled={categorising}
                                className="btn-secondary"
                                data-testid="auto-categorise-btn"
                                title="Use AI to assign category headings to every menu item"
                            >
                                {categorising ? "🤖 Categorising…" : "🤖 Auto-categorise"}
                            </button>
                        </div>
                    )}
                </div>

                {(!brand.items || brand.items.length === 0) ? (
                    <div className="border border-dashed border-[var(--border)] p-12 text-center">
                        <p className="text-muted mb-4">No items yet.</p>
                        <button onClick={() => setShowImport(true)} className="btn-primary mr-3" data-testid="empty-import-btn">
                            📄 Import PDF or menu photo
                        </button>
                        <button onClick={() => setEditingItem("new")} className="btn-secondary">
                            + Add one manually
                        </button>
                    </div>
                ) : (
                    <SortableMenuList
                        brand={brand}
                        onEdit={(it) => setEditingItem(it)}
                        onDuplicate={(it) => {
                            // Open ItemEditor prefilled with a copy of `it` (no id
                            // → save endpoint creates a fresh row). Name gets a
                            // " (Copy)" suffix so admin can spot the dupe in the
                            // list and rename before saving.
                            const { id, ...rest } = it || {};
                            setEditingItem({ ...rest, name: `${rest.name || "Item"} (Copy)` });
                        }}
                        onRefresh={refresh}
                    />
                )}
            </section>

            {editingItem && (
                <ItemEditor
                    brand={brand}
                    item={editingItem === "new" ? null : editingItem}
                    allergens={allergens}
                    onClose={() => setEditingItem(null)}
                    onSaved={() => { setEditingItem(null); refresh(); }}
                />
            )}
            {showSavedOptions && (
                <SavedOptionsModal
                    brand={brand}
                    Editor={ModifierGroupsEditor}
                    onClose={() => setShowSavedOptions(false)}
                    onChanged={refresh}
                />
            )}
            {showImport && (
                <ImportMenuModal
                    brand={brand}
                    allergens={allergens}
                    onClose={() => setShowImport(false)}
                    onImported={() => { setShowImport(false); refresh(); }}
                />
            )}
            {showReports && (
                <ReportsModal
                    brand={brand}
                    onClose={() => setShowReports(false)}
                />
            )}
            {showQR && (
                <QRCodeModal
                    brand={brand}
                    baseUrl={window.location.origin}
                    onClose={() => setShowQR(false)}
                />
            )}
        </AdminShell>
    );
};

const DAYS_ORDERED = [
    { key: "monday", label: "Monday" },
    { key: "tuesday", label: "Tuesday" },
    { key: "wednesday", label: "Wednesday" },
    { key: "thursday", label: "Thursday" },
    { key: "friday", label: "Friday" },
    { key: "saturday", label: "Saturday" },
    { key: "sunday", label: "Sunday" },
];

const OpeningHoursAndLaunchEditor = ({ openingHours, setOpeningHours, comingSoon, setComingSoon, temporarilyClosed, setTemporarilyClosed }) => {
    const setDay = (day, key, value) => {
        const next = { ...(openingHours || {}) };
        // Editing one day from 24/7: keep the rest open all day rather than silently closed.
        if (Object.keys(next).length === 0) DAYS_ORDERED.forEach(({ key: k }) => { next[k] = { open: "00:00", close: "23:59" }; });
        const existing = next[day] || {};
        next[day] = { ...existing, [key]: value };
        delete next[day].closed;
        setOpeningHours(next);
    };
    // Explicit {closed: true} so "all days closed" never collapses to {} (= 24/7).
    const toggleClosed = (day, closed) => {
        const next = { ...(openingHours || {}) };
        // Coming from 24/7: keep every other day open all day, otherwise they'd read as closed.
        if (Object.keys(next).length === 0) DAYS_ORDERED.forEach(({ key }) => { next[key] = { open: "00:00", close: "23:59" }; });
        next[day] = closed ? { closed: true } : { open: "11:00", close: "22:00" };
        setOpeningHours(next);
    };
    const isOpenAll = openingHours && Object.keys(openingHours).length === 0;
    const applyStandard = () => {
        const standard = {};
        DAYS_ORDERED.forEach(({ key }) => { standard[key] = { open: "11:00", close: "22:00" }; });
        setOpeningHours(standard);
    };

    return (
        <section className="mt-7 pt-5 border-t border-[var(--border)]" data-testid="brand-hours-editor">
            <div className="flex items-baseline justify-between gap-4 mb-4">
                <div>
                    <p className="eyebrow mb-1">// Availability &amp; opening hours</p>
                    <p className="text-muted text-[12px] max-w-lg">
                        When closed, the QR menu shows an "Opening at HH:MM" splash and blocks new orders. Leave all days blank for 24/7 (default). Use the coming-soon flag for brands that haven't launched yet.
                    </p>
                </div>
                <div className="flex gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={applyStandard}
                        className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-gold"
                        data-testid="brand-hours-standard"
                    >
                        Fill 11:00–22:00
                    </button>
                    <button
                        type="button"
                        onClick={() => setOpeningHours({})}
                        className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-gold"
                        data-testid="brand-hours-clear"
                    >
                        Clear (24/7)
                    </button>
                </div>
            </div>

            <label
                className={`flex items-center gap-3 mb-4 cursor-pointer border px-4 py-3 transition-colors ${temporarilyClosed ? "border-[#ff5246] bg-[#ff5246]/10" : "border-[var(--border)]"}`}
                data-testid="brand-closed-now-toggle"
            >
                <input
                    type="checkbox"
                    checked={!!temporarilyClosed}
                    onChange={(e) => setTemporarilyClosed(e.target.checked)}
                    className="w-5 h-5"
                    data-testid="brand-closed-now-checkbox"
                />
                <span className="font-mono text-[12px] uppercase tracking-[0.18em]">
                    <span className="text-[#ff5246] font-bold">Closed now — stop all orders</span>
                    <span className="text-muted"> — blocks QR, kiosk, POS &amp; pre-orders until you untick it</span>
                </span>
            </label>

            <label className="flex items-center gap-3 mb-4 cursor-pointer" data-testid="brand-coming-soon-toggle">
                <input
                    type="checkbox"
                    checked={!!comingSoon}
                    onChange={(e) => setComingSoon(e.target.checked)}
                    className="w-4 h-4"
                />
                <span className="font-mono text-[12px] uppercase tracking-[0.18em]">
                    <span className="text-gold">Opening soon</span>
                    <span className="text-muted"> — blocks orders on the QR menu &amp; shows a "coming soon" splash</span>
                </span>
            </label>

            <div className="space-y-2">
                {isOpenAll && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted py-2">
                        No hours set — this brand is treated as OPEN 24/7 for orders.
                    </p>
                )}
                {DAYS_ORDERED.map(({ key, label }) => {
                    const day = (openingHours || {})[key] || {};
                    const isClosed = !isOpenAll && (day.closed || !day.open);
                    return (
                        <div key={key} className="flex items-center gap-3" data-testid={`brand-hours-row-${key}`}>
                            <div className="w-24 font-mono text-[11px] uppercase tracking-[0.16em] text-muted">
                                {label}
                            </div>
                            <input
                                type="time"
                                value={day.open || ""}
                                onChange={(e) => setDay(key, "open", e.target.value)}
                                disabled={isClosed}
                                className="input w-28 text-[12px] disabled:opacity-30"
                                aria-label={`${label} open time`}
                                data-testid={`brand-hours-${key}-open`}
                            />
                            <span className="text-muted text-[11px]">→</span>
                            <input
                                type="time"
                                value={day.close || ""}
                                onChange={(e) => setDay(key, "close", e.target.value)}
                                disabled={isClosed}
                                className="input w-28 text-[12px] disabled:opacity-30"
                                aria-label={`${label} close time`}
                                data-testid={`brand-hours-${key}-close`}
                            />
                            <label className={`flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] cursor-pointer ${isClosed ? "text-[#ff5246]" : "text-muted"}`}>
                                <input
                                    type="checkbox"
                                    checked={isClosed}
                                    onChange={(e) => toggleClosed(key, e.target.checked)}
                                    data-testid={`brand-hours-${key}-closed`}
                                />
                                Closed
                            </label>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

const BrandSettingsPanel = ({ brand, saving, onSave, isMaster, onDelete }) => {
    const [form, setForm] = useState({
        tagline: brand.tagline || "",
        accent: brand.accent || "#E8B339",
        image_url: brand.image_url || "",
        halal: brand.halal,
        active: brand.active,
        daily_special: brand.daily_special || "",
        eta_min: brand.eta_min || 0,
        badges: brand.badges || [],
        opening_hours: brand.opening_hours || {},
        coming_soon: brand.coming_soon || false,
        temporarily_closed: brand.temporarily_closed || false,
    });
    const setF = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
    const dirty = JSON.stringify({ ...form }) !== JSON.stringify({
        tagline: brand.tagline || "", accent: brand.accent || "#E8B339",
        image_url: brand.image_url || "", halal: brand.halal, active: brand.active,
        daily_special: brand.daily_special || "", eta_min: brand.eta_min || 0,
        badges: brand.badges || [],
        opening_hours: brand.opening_hours || {},
        coming_soon: brand.coming_soon || false,
        temporarily_closed: brand.temporarily_closed || false,
    });

    const addBadge = () => setForm((f) => ({
        ...f,
        badges: [...f.badges, { id: Math.random().toString(36).slice(2, 10), type: "pct_off", min_spend: 10, value: 20, label: "", active: true, applies_to: "both" }],
    }));
    const updateBadge = (idx, patch) => setForm((f) => ({
        ...f,
        badges: f.badges.map((b, i) => i === idx ? { ...b, ...patch } : b),
    }));
    const removeBadge = (idx) => setForm((f) => ({
        ...f,
        badges: f.badges.filter((_, i) => i !== idx),
    }));

    return (
        <section className="bg-[var(--surface)] border border-[var(--border)] p-6">
            <p className="eyebrow mb-4">// Brand settings</p>
            <div className="grid md:grid-cols-2 gap-5">
                <Field label="Tagline">
                    <input className="input" value={form.tagline} onChange={setF("tagline")} data-testid="brand-tagline" />
                </Field>
                <Field label="Hero image">
                    <ImageUploadField
                        value={form.image_url}
                        onChange={(v) => setForm({ ...form, image_url: v })}
                        folder="naami/brands"
                        label=""
                        testId="brand-image-upload"
                    />
                </Field>
                <Field label="Accent colour">
                    <div className="flex gap-2 items-center">
                        <input type="color" className="h-12 w-16 bg-transparent border border-[var(--border)]" value={form.accent} onChange={setF("accent")} />
                        <input className="input flex-1" value={form.accent} onChange={setF("accent")} />
                    </div>
                </Field>
                <Field label="Daily special banner (shown to customers)">
                    <input className="input" value={form.daily_special} onChange={setF("daily_special")} placeholder='e.g. "Today only: £8 lunch combo"' data-testid="brand-daily-special" />
                </Field>
                <Field label="ETA bubble (minutes — shown on brand card)">
                    <input type="number" min="0" max="120" className="input" value={form.eta_min} onChange={setF("eta_min")} placeholder="0 = hide" data-testid="brand-eta-min" />
                </Field>
                <div className="flex gap-6 items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.halal} onChange={setF("halal")} className="accent-[var(--gold)] w-5 h-5" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Halal</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.active} onChange={setF("active")} className="accent-[var(--gold)] w-5 h-5" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Active (visible to customers)</span>
                    </label>
                </div>
            </div>

            <BadgesEditor
                badges={form.badges}
                onAdd={addBadge}
                onUpdate={updateBadge}
                onRemove={removeBadge}
            />

            <OpeningHoursAndLaunchEditor
                openingHours={form.opening_hours}
                setOpeningHours={(h) => setForm((f) => ({ ...f, opening_hours: h }))}
                comingSoon={form.coming_soon}
                setComingSoon={(v) => setForm((f) => ({ ...f, coming_soon: v }))}
                temporarilyClosed={form.temporarily_closed}
                setTemporarilyClosed={(v) => setForm((f) => ({ ...f, temporarily_closed: v }))}
            />

            <div className="flex items-center justify-between mt-6 pt-5 border-t border-[var(--border)]">
                <div className="flex gap-3">
                    <button
                        onClick={() => onSave(form)}
                        disabled={!dirty || saving}
                        className="btn-primary"
                        data-testid="brand-save"
                    >
                        {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
                {isMaster && (
                    <button
                        type="button"
                        onClick={onDelete}
                        className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff5246] hover:underline"
                        data-testid="brand-delete"
                    >
                        Delete brand
                    </button>
                )}
            </div>
        </section>
    );
};

const CategorisedItemsGrid = ({ items, onEdit, onDuplicate }) => {
    const groups = groupItemsByCategory(items);
    return (
        <div data-testid="admin-items-grid">
            {groups.map(({ category, items: catItems }) => (
                <div key={category} className="mb-8" data-testid={`admin-cat-${category}`}>
                    <div className="flex items-baseline justify-between mb-3 pb-2 border-b border-[var(--border)]">
                        <h3 className="font-display text-[20px]" style={{ color: category === UNCATEGORISED_LABEL ? "var(--muted)" : "var(--gold)" }}>
                            {category}
                        </h3>
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">
                            {catItems.length} {catItems.length === 1 ? "item" : "items"}
                        </span>
                    </div>
                    <div
                        className="grid gap-3"
                        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}
                    >
                        {catItems.map((it) => (
                            <ItemCard key={it.id} item={it} onEdit={() => onEdit(it)} onDuplicate={() => onDuplicate(it)} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

const ItemCard = ({ item, onEdit, onDuplicate }) => (
    <div
        className="relative group bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] transition-colors text-left flex overflow-hidden cursor-pointer"
        onClick={onEdit}
        data-testid={`admin-item-${item.id}`}
    >
        {item.image_url && (
            <div className="w-28 shrink-0 bg-[var(--surface-elevated)] aspect-square overflow-hidden">
                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
            </div>
        )}
        <div className="flex-1 min-w-0 p-4">
            <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-display text-[16px] truncate flex-1">{item.name}</h3>
                {!item.available && (
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#ff5246] border border-[#ff5246] px-1.5 py-0.5">
                        86&apos;d
                    </span>
                )}
            </div>
            <p className="text-[12.5px] text-muted line-clamp-2 mb-2">{item.description}</p>
            <div className="flex items-center gap-3">
                <span className="font-mono text-[13px] text-gold">{formatMoney(item.price)}</span>
                {item.popular && (
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-gold">★ Popular</span>
                )}
                {item.is_special && (
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--status-routing)]">Special</span>
                )}
                {item.allergens?.length > 0 && (
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted">
                        ⚠ {item.allergens.length}
                    </span>
                )}
            </div>
        </div>
        {onDuplicate && (
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-1 border border-[var(--border)] bg-[var(--bg)]/80 hover:border-[var(--gold)] hover:text-gold"
                data-testid={`admin-item-duplicate-${item.id}`}
                title="Duplicate this item"
            >
                ⧉ Duplicate
            </button>
        )}
    </div>
);

const ItemEditor = ({ brand, item, allergens, onClose, onSaved }) => {
    const [form, setForm] = useState({
        name: item?.name || "",
        description: item?.description || "",
        price: item?.price ?? "",
        calories: item?.calories ?? "",
        image_url: item?.image_url || "",
        popular: item?.popular || false,
        available: item?.available !== false,
        is_special: item?.is_special || false,
        category: item?.category || "",
        allergens: item?.allergens || [],
        tags: item?.tags || [],
        modifier_groups: item?.modifier_groups || [],
    });
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);
    const setF = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const toggleAllergen = (a) => {
        setForm((f) => ({
            ...f,
            allergens: f.allergens.includes(a)
                ? f.allergens.filter((x) => x !== a)
                : [...f.allergens, a],
        }));
    };

    const toggleTag = (t) => {
        setForm((f) => ({
            ...f,
            tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr("");
        try {
            const payload = {
                ...form,
                price: parseFloat(form.price),
                calories: form.calories === "" ? null : parseInt(form.calories, 10),
            };
            if (item && item.id) {
                await adminUpdateItem(brand.id, item.id, payload);
            } else {
                // No id → either a brand-new item OR a duplicated item
                // (we stripped the id in the onDuplicate handler so save
                // creates a fresh row).
                await adminAddItem(brand.id, payload);
            }
            onSaved();
        } catch (ex) {
            setErr(ex.response?.data?.detail || "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!item || !item.id || !window.confirm(`Delete "${item.name}"?`)) return;
        await adminDeleteItem(brand.id, item.id);
        onSaved();
    };

    return (
        <Modal title={item?.id ? "Edit item" : item ? "Duplicate item" : "New item"} onClose={onClose}>
            <form onSubmit={submit} className="space-y-4">
                <Field label="Name"><input className="input" required value={form.name} onChange={setF("name")} data-testid="item-name" /></Field>
                <Field label="Description"><textarea className="input" rows="2" value={form.description} onChange={setF("description")} data-testid="item-desc" /></Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Price (£)"><input className="input" required type="number" step="0.01" min="0" value={form.price} onChange={setF("price")} data-testid="item-price" /></Field>
                    <Field label="Calories (optional)"><input className="input" type="number" value={form.calories} onChange={setF("calories")} /></Field>
                </div>
                <Field label="Category (heading on customer menu)">
                    <input className="input" value={form.category} onChange={setF("category")} placeholder="e.g. Burgers, Wraps, Sides, Drinks" data-testid="item-category" />
                </Field>
                <Field label="Photo">
                    <ImageUploadField
                        value={form.image_url}
                        onChange={(v) => setForm({ ...form, image_url: v })}
                        folder="naami/items"
                        label=""
                        testId="item-image-upload"
                    />
                </Field>
                <Field label="Allergens">
                    <div className="flex flex-wrap gap-2">
                        {allergens.map((a) => (
                            <button
                                type="button"
                                key={a}
                                onClick={() => toggleAllergen(a)}
                                className={`font-mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border ${
                                    form.allergens.includes(a)
                                        ? "border-[var(--gold)] text-gold bg-[var(--gold)]/10"
                                        : "border-[var(--border)] text-muted hover:border-[var(--gold)]"
                                }`}
                                data-testid={`allergen-${a}`}
                            >
                                {ALLERGEN_LABELS[a] || a}
                            </button>
                        ))}
                    </div>
                </Field>

                <Field label="Diet & badge tags (shown on the menu)">
                    <div className="flex flex-wrap gap-2">
                        {ITEM_TAG_OPTIONS.map((t) => (
                            <button
                                type="button"
                                key={t.value}
                                onClick={() => toggleTag(t.value)}
                                className={`font-mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border flex items-center gap-1.5 ${
                                    form.tags.includes(t.value)
                                        ? "border-[var(--gold)] text-gold bg-[var(--gold)]/10"
                                        : "border-[var(--border)] text-muted hover:border-[var(--gold)]"
                                }`}
                                data-testid={`item-tag-${t.value}`}
                            >
                                <span>{t.icon}</span>
                                <span>{t.label}</span>
                            </button>
                        ))}
                    </div>
                </Field>

                <SavedOptionsPicker
                    brand={brand}
                    groups={form.modifier_groups}
                    onAdd={(g) => setForm((f) => ({ ...f, modifier_groups: [...(f.modifier_groups || []), g] }))}
                />
                <ModifierGroupsEditor
                    brand={brand}
                    groups={form.modifier_groups}
                    onChange={(groups) => setForm({ ...form, modifier_groups: groups })}
                />
                <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.available} onChange={setF("available")} className="accent-[var(--gold)] w-5 h-5" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Available</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.popular} onChange={setF("popular")} className="accent-[var(--gold)] w-5 h-5" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Popular</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.is_special} onChange={setF("is_special")} className="accent-[var(--gold)] w-5 h-5" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Today&apos;s special</span>
                    </label>
                </div>
                {err && <p className="text-[#ff5246] text-sm font-mono">{err}</p>}
                <div className="flex items-center justify-between pt-2 border-t border-[var(--border)] mt-4">
                    {item?.id ? (
                        <button type="button" onClick={handleDelete} className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff5246] hover:underline" data-testid="item-delete">
                            Delete
                        </button>
                    ) : <span />}
                    <button disabled={saving} className="btn-primary" data-testid="item-save">
                        {saving ? "Saving…" : item?.id ? "Save changes" : "Create item"}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

const ImportMenuModal = ({ brand, allergens, onClose, onImported }) => {
    const [file, setFile] = useState(null);
    const [url, setUrl] = useState("");
    const [extracting, setExtracting] = useState(false);
    const [extracted, setExtracted] = useState(null); // { items: [...], error?, source_url? }
    const [scrapeBlocked, setScrapeBlocked] = useState(false);
    const [replace, setReplace] = useState(false);
    const [importing, setImporting] = useState(false);
    const [err, setErr] = useState("");
    const fileRef = useRef(null);

    const extract = async () => {
        if (!file) return;
        setExtracting(true);
        setErr("");
        setScrapeBlocked(false);
        try {
            const r = await adminExtractMenu(brand.id, file);
            setExtracted(r);
            if (r.error) setErr(r.error);
        } catch (ex) {
            setErr(ex.response?.data?.detail || "Extraction failed. Check the file is readable.");
        } finally {
            setExtracting(false);
        }
    };

    const extractFromUrl = async () => {
        if (!url.trim()) return;
        setExtracting(true);
        setErr("");
        setScrapeBlocked(false);
        try {
            const r = await adminImportMenuFromUrl(brand.id, url.trim());
            // Any scrape failure (Cloudflare, browser crash, DNS, etc) → show
            // the friendly banner + steer to file upload instead of dumping
            // a raw stack trace on the operator.
            if (r.blocked) {
                setScrapeBlocked(true);
                setErr("");
                setExtracted(null);
                setTimeout(() => fileRef.current?.focus?.(), 100);
                return;
            }
            if (!r.items || r.items.length === 0) {
                setErr(r.error || "No items extracted from that URL. Try a direct link to the restaurant's menu page.");
                setExtracted(null);
                return;
            }
            setExtracted(r);
            if (r.error) setErr(r.error);
        } catch (ex) {
            setErr(ex.response?.data?.detail || "URL import failed. Please check the URL is publicly accessible.");
        } finally {
            setExtracting(false);
        }
    };

    const toggleRowAllergen = (idx, a) => {
        setExtracted((e) => {
            const next = { ...e, items: e.items.map((it, i) => i === idx ? {
                ...it,
                allergens: (it.allergens || []).includes(a)
                    ? it.allergens.filter((x) => x !== a)
                    : [...(it.allergens || []), a],
            } : it) };
            return next;
        });
    };

    const updateRow = (idx, patch) => {
        setExtracted((e) => ({
            ...e,
            items: e.items.map((it, i) => i === idx ? { ...it, ...patch } : it),
        }));
    };

    const removeRow = (idx) => {
        setExtracted((e) => ({ ...e, items: e.items.filter((_, i) => i !== idx) }));
    };

    const doImport = async () => {
        if (!extracted?.items?.length) return;
        setImporting(true);
        setErr("");
        try {
            // URL-imported items may carry an image_url — route those through
            // the URL-commit endpoint so the images get re-hosted to Cloudinary.
            // PDF-imported items go through the existing bulk-add path.
            const isFromUrl = !!extracted.source_url || extracted.items.some((it) => it.image_url);
            if (isFromUrl) {
                const items = extracted.items.map((it) => ({
                    name: it.name,
                    description: it.description || "",
                    price: parseFloat(it.price),
                    category: (it.category || "").trim(),
                    image_url: it.image_url || "",
                    popular: !!it.popular,
                    modifier_groups: it.modifier_groups || [],
                }));
                await adminImportMenuCommit(brand.id, items);
            } else {
                const items = extracted.items.map((it) => ({
                    name: it.name,
                    description: it.description || "",
                    price: parseFloat(it.price),
                    calories: it.calories || null,
                    popular: !!it.popular,
                    available: true,
                    allergens: it.allergens || [],
                    category: (it.category || "").trim(),
                }));
                await adminBulkAddItems(brand.id, items, replace);
            }
            onImported();
        } catch (ex) {
            setErr(ex.response?.data?.detail || "Import failed");
        } finally {
            setImporting(false);
        }
    };

    return (
        <Modal title={`Import menu · ${brand.name}`} onClose={onClose}>
            {!extracted ? (
                <div>
                    <p className="text-[14px] text-[var(--text)]/80 mb-5 leading-relaxed">
                        Import a menu two ways. AI will read item names, descriptions, prices &amp; categories, then you review before saving.
                    </p>

                    {/* URL import — Just Eat, Deliveroo, Uber Eats, or the restaurant's own site */}
                    <div className="border border-[var(--border)] p-4 mb-4" data-testid="import-url-panel">
                        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mb-2">// Import from URL</div>
                        <p className="text-[13px] text-[var(--text)]/70 mb-3 leading-snug">
                            Paste a Just Eat, Deliveroo, Uber Eats or restaurant menu URL. We&apos;ll extract items + prices + photos and re-host any images so they don&apos;t break later.
                        </p>
                        <div className="flex gap-2">
                            <input
                                type="url"
                                className="input flex-1"
                                placeholder="https://www.just-eat.co.uk/restaurants-…"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                data-testid="import-url-input"
                            />
                            <button
                                onClick={extractFromUrl}
                                disabled={extracting || !url.trim()}
                                className="btn-primary text-xs px-4 disabled:opacity-40"
                                data-testid="import-url-btn"
                            >
                                {extracting ? "Fetching…" : "Import →"}
                            </button>
                        </div>
                    </div>

                    <div className="text-center font-mono text-[10px] uppercase tracking-[0.22em] text-muted my-3">— or —</div>

                    {scrapeBlocked && (
                        <div
                            className="border-2 border-[var(--gold)] bg-[var(--gold)]/10 p-4 mb-4"
                            data-testid="scrape-blocked-banner"
                        >
                            <div className="flex items-start gap-3">
                                <span className="text-2xl leading-none">⚠️</span>
                                <div className="flex-1">
                                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold mb-1">
                                        // URL scrape didn't work
                                    </div>
                                    <p className="text-[13px] leading-relaxed mb-3">
                                        We couldn't grab the menu automatically — either the site's bot-protection
                                        blocked us or our scraper crashed.
                                        <strong className="text-gold"> Fastest fix — do this in 30 seconds:</strong>
                                    </p>
                                    <ol className="text-[13px] leading-relaxed space-y-1 list-decimal ml-5 mb-3">
                                        <li>Open <a href={url} target="_blank" rel="noreferrer" className="underline text-gold">the menu page</a> in a new tab</li>
                                        <li>Take a screenshot of the menu (iPhone: hold power + volume up · Windows: <kbd className="font-mono px-1 border border-[var(--border)]">Win+Shift+S</kbd> · Mac: <kbd className="font-mono px-1 border border-[var(--border)]">⌘+Shift+4</kbd>). Or use browser Print → Save as PDF for the whole page.</li>
                                        <li>Drop it in the box below ↓ — Gemini reads the image the same way it reads the URL.</li>
                                    </ol>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                                        Screenshots work every time — same result, no scraper required.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div
                        onClick={() => fileRef.current?.click()}
                        className="border-2 border-dashed border-[var(--border-strong)] hover:border-[var(--gold)] p-10 text-center cursor-pointer transition-colors mb-5"
                        data-testid="pdf-dropzone"
                    >
                        <input
                            ref={fileRef}
                            type="file"
                            accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="hidden"
                            data-testid="pdf-input"
                        />
                        {file ? (
                            <>
                                <p className="font-display text-[18px] mb-1">{file.name}</p>
                                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                                    {(file.size / 1024).toFixed(0)} KB · Click to change
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="font-display text-[20px] mb-2 text-gold">Drop a PDF or menu photo</p>
                                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                                    PDF, JPG, PNG, WEBP or HEIC — click to select
                                </p>
                            </>
                        )}
                    </div>
                    {err && <p className="text-[#ff5246] text-sm font-mono mb-3">{err}</p>}
                    <button
                        onClick={extract}
                        disabled={!file || extracting}
                        className="btn-primary w-full justify-center"
                        data-testid="extract-pdf-btn"
                    >
                        {extracting ? "🤖 Reading menu…" : "Extract items →"}
                    </button>
                    {extracting && (
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted text-center mt-4 animate-pulse">
                            This usually takes 10–30 seconds…
                        </p>
                    )}
                </div>
            ) : (
                <div>
                    <p className="eyebrow mb-3">// {extracted.items?.length || 0} items extracted</p>
                    {extracted.source === "just-eat-next-data" && (
                        <p className={`font-mono text-[11px] mb-3 ${extracted.items_with_modifiers ? "text-gold" : "text-muted"}`} data-testid="import-modifiers-summary">
                            {extracted.items_with_modifiers
                                ? `✓ Options (sauces, drinks, sizes) found for ${extracted.items_with_modifiers} items — imported with them.`
                                : extracted.modifiers_note}
                        </p>
                    )}
                    <p className="text-[13.5px] text-[var(--text)]/70 mb-5 leading-relaxed">
                        Review each row, edit anything wrong, tag allergens, then save. You can add photos
                        after import.
                    </p>

                    <div className="max-h-[50vh] overflow-y-auto border border-[var(--border)] divide-y divide-[var(--border)] mb-4">
                        {(extracted.items || []).map((it, idx) => (
                            <div key={idx} className="p-3" data-testid={`extracted-row-${idx}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <input
                                        className="input flex-1 min-w-0"
                                        value={it.name}
                                        onChange={(e) => updateRow(idx, { name: e.target.value })}
                                        placeholder="Name"
                                        data-testid={`extracted-name-${idx}`}
                                    />
                                    <input
                                        className="input !w-24 shrink-0"
                                        type="number"
                                        step="0.01"
                                        value={it.price}
                                        onChange={(e) => updateRow(idx, { price: e.target.value })}
                                        data-testid={`extracted-price-${idx}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeRow(idx)}
                                        className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff5246] hover:underline px-2"
                                    >
                                        Remove
                                    </button>
                                </div>
                                <textarea
                                    className="input mb-2 text-[13px]"
                                    rows="1"
                                    value={it.description}
                                    onChange={(e) => updateRow(idx, { description: e.target.value })}
                                    placeholder="Description"
                                />
                                <input
                                    className="input mb-2 text-[12px] h-8"
                                    value={it.category || ""}
                                    onChange={(e) => updateRow(idx, { category: e.target.value })}
                                    placeholder="Category (e.g. Burgers, Sides) — auto-detected from PDF/photo"
                                    data-testid={`extracted-category-${idx}`}
                                />
                                {it.modifier_groups?.length > 0 && (
                                    <p className="font-mono text-[10px] text-gold mb-2" data-testid={`extracted-mods-${idx}`}>
                                        + {it.modifier_groups.map((g) => `${g.name} (${g.options.length})`).join(" · ")}
                                    </p>
                                )}
                                <div className="flex flex-wrap gap-1">
                                    {allergens.map((a) => (
                                        <button
                                            type="button"
                                            key={a}
                                            onClick={() => toggleRowAllergen(idx, a)}
                                            className={`font-mono text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border ${
                                                (it.allergens || []).includes(a)
                                                    ? "border-[var(--gold)] text-gold bg-[var(--gold)]/10"
                                                    : "border-[var(--border)] text-muted"
                                            }`}
                                        >
                                            {ALLERGEN_LABELS[a] || a}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <label className="flex items-center gap-2 mb-4 cursor-pointer">
                        <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="accent-[var(--gold)] w-5 h-5" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em]">
                            Replace existing menu (otherwise add to it)
                        </span>
                    </label>

                    {err && <p className="text-[#ff5246] text-sm font-mono mb-3">{err}</p>}

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => { setExtracted(null); setFile(null); }}
                            className="btn-secondary"
                        >
                            Start over
                        </button>
                        <button
                            onClick={doImport}
                            disabled={importing || !extracted.items?.length}
                            className="btn-primary flex-1 justify-center"
                            data-testid="import-confirm"
                        >
                            {importing ? "Importing…" : `Import ${extracted.items?.length || 0} items →`}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

const Field = ({ label, children }) => (
    <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-2">{label}</span>
        {children}
    </label>
);

const ReportsModal = ({ brand, onClose }) => {
    const [tab, setTab] = useState("payout");   // 'payout' (default) | 'daily'

    return (
        <Modal title={`Reports · ${brand.name}`} onClose={onClose}>
            <div className="flex gap-2 mb-4 border-b border-[var(--border)]" data-testid="reports-tabs">
                <TabBtn active={tab === "payout"} onClick={() => setTab("payout")} testid="reports-tab-payout">
                    Payout statement
                </TabBtn>
                <TabBtn active={tab === "daily"} onClick={() => setTab("daily")} testid="reports-tab-daily">
                    Daily snapshot
                </TabBtn>
            </div>
            {tab === "payout" ? <PayoutView brand={brand} /> : <DailyView brand={brand} />}
        </Modal>
    );
};

const TabBtn = ({ active, onClick, children, testid }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className={`px-3 pb-2 -mb-px font-mono text-[11px] uppercase tracking-[0.22em] border-b-2 transition-colors ${
            active ? "text-gold border-gold" : "text-muted border-transparent hover:text-white"
        }`}
    >
        {children}
    </button>
);

/** Full payout statement — date range, commission split, PDF ready to send to the brand. */
const PayoutView = ({ brand }) => {
    const [fromDate, setFromDate] = useState(() => {
        // Default range: last 7 days ending yesterday, so the "current payout period" is prefilled.
        const d = new Date(); d.setDate(d.getDate() - 7);
        return d.toISOString().slice(0, 10);
    });
    const [toDate, setToDate] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 1);
        return d.toISOString().slice(0, 10);
    });
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true); setErr("");
        try {
            const r = await adminPayoutReportJson(brand.id, fromDate, toDate);
            setReport(r);
        } catch (ex) {
            setErr(ex.response?.data?.detail || "Failed to load payout report");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromDate, toDate]);

    const downloadPdf = async () => {
        const blob = await adminPayoutReportPdfBlob(brand.id, fromDate, toDate);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const rng = fromDate === toDate ? fromDate : `${fromDate}_${toDate}`;
        a.download = `naami-payout-${brand.slug}-${rng}.pdf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const setLastNDays = (n) => {
        const to = new Date(); to.setDate(to.getDate() - 1);
        const from = new Date(to); from.setDate(from.getDate() - (n - 1));
        setFromDate(from.toISOString().slice(0, 10));
        setToDate(to.toISOString().slice(0, 10));
    };

    const t = report?.totals;
    const isRange = report && report.from_date !== report.to_date;

    return (
        <div>
            <div className="flex flex-wrap gap-3 items-end">
                <Field label="From">
                    <input
                        className="input"
                        type="date"
                        value={fromDate}
                        onChange={(e) => {
                            const v = e.target.value;
                            setFromDate(v);
                            if (toDate && v > toDate) setToDate(v);
                        }}
                        data-testid="payout-from-date"
                    />
                </Field>
                <Field label="To">
                    <input
                        className="input"
                        type="date"
                        value={toDate}
                        min={fromDate}
                        onChange={(e) => setToDate(e.target.value)}
                        data-testid="payout-to-date"
                    />
                </Field>
                <div className="flex gap-1 pb-[2px]">
                    {[7, 14, 30].map((n) => (
                        <button
                            key={n}
                            onClick={() => setLastNDays(n)}
                            className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-[var(--border)] hover:border-gold hover:text-gold"
                            data-testid={`payout-preset-${n}`}
                        >
                            {n}d
                        </button>
                    ))}
                </div>
            </div>

            {loading && <p className="text-muted mt-4">Crunching numbers…</p>}
            {err && <p className="text-[#ff5246] text-sm font-mono mt-4">{err}</p>}

            {report && !loading && (
                <div className="mt-5 space-y-4" data-testid="payout-data">
                    {/* Headline KPIs — 4 cards; payout is the hero */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Kpi label="Orders" value={t.orders} />
                        <Kpi label="Gross" value={`£${t.brand_gross.toFixed(2)}`} />
                        <Kpi label="Commission" value={`−£${t.commission.toFixed(2)}`} />
                        <Kpi label="Payout owed" value={`£${t.payout.toFixed(2)}`} highlight testid="payout-total" />
                    </div>

                    {/* Full stacked breakdown so operators can eyeball the maths.
                        Commission is charged on GROSS, so it's deducted directly
                        alongside discounts — no "net revenue" intermediate row. */}
                    <div className="border border-[var(--border)] p-4 space-y-1.5 font-mono text-[12px]">
                        <Row label="Gross sales" val={`£${t.brand_gross.toFixed(2)}`} strong />
                        <Row label="− Discounts & promo codes" val={`−£${t.brand_discounts.toFixed(2)}`} muted />
                        <Row label="− Platform commission (on gross)" val={`−£${t.commission.toFixed(2)}`} muted />
                        <div className="pt-2 mt-1 border-t border-[var(--border)]">
                            <Row label="= Payout owed" val={`£${t.payout.toFixed(2)}`} gold />
                        </div>
                    </div>

                    {/* Order-type split */}
                    {Object.keys(t.by_type || {}).length > 0 && (
                        <div>
                            <p className="eyebrow mb-2">
                                // Breakdown by order type · Delivery {report.commission_rates.delivery}% · Others {report.commission_rates.default}%
                            </p>
                            <div className="border border-[var(--border)] divide-y divide-[var(--border)] text-[12px]">
                                {Object.entries(t.by_type).map(([otype, v]) => (
                                    <div key={otype} className="p-2.5 grid grid-cols-6 gap-2 font-mono" data-testid={`payout-type-${otype}`}>
                                        <span className="uppercase tracking-[0.12em] text-muted">{otype.replace("_", " ")}</span>
                                        <span className="text-right">{v.orders} ord</span>
                                        <span className="text-right">£{v.gross.toFixed(2)}</span>
                                        <span className="text-right text-muted">−£{v.discounts.toFixed(2)}</span>
                                        <span className="text-right text-muted">−£{v.commission.toFixed(2)}</span>
                                        <span className="text-right text-gold">£{v.payout.toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {report.top_items?.length > 0 && (
                        <div>
                            <p className="eyebrow mb-2">// Top items</p>
                            <div className="border border-[var(--border)] divide-y divide-[var(--border)]">
                                {report.top_items.slice(0, 8).map((it, i) => (
                                    <div key={i} className="p-2.5 flex items-center justify-between text-[12px]">
                                        <span className="truncate">{i + 1}. {it.name}</span>
                                        <span className="font-mono text-muted whitespace-nowrap">
                                            {it.qty}× · £{it.revenue.toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-3 pt-4 border-t border-[var(--border)]">
                        <button onClick={downloadPdf} className="btn-primary" data-testid="payout-download-pdf">
                            ⬇ Download PDF ({isRange ? `${report.from_date} → ${report.to_date}` : report.from_date})
                        </button>
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                        Paid orders only · Commission on gross sales · Discounts split proportionally to this brand's share of each order
                    </p>
                </div>
            )}
        </div>
    );
};

/** Legacy quick-look daily report — kept for spot-checks and hourly split. */
const DailyView = ({ brand }) => {
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [emailTo, setEmailTo] = useState("");
    const [sending, setSending] = useState(false);
    const [sendMsg, setSendMsg] = useState("");

    const load = async () => {
        setLoading(true); setErr("");
        try {
            const r = await adminReportJson(brand.id, date);
            setReport(r);
        } catch (ex) {
            setErr(ex.response?.data?.detail || "Failed");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

    const downloadPdf = async () => {
        const blob = await adminReportPdfBlob(brand.id, date);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `naami-${brand.slug}-${date}.pdf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const sendEmail = async () => {
        if (!emailTo.includes("@")) { setSendMsg("Invalid email"); return; }
        setSending(true); setSendMsg("");
        try {
            const r = await adminSendReport(brand.id, emailTo, date);
            setSendMsg(r.mocked
                ? `📨 Queued for ${r.to}. Email delivery is MOCKED — wire SendGrid/Resend to actually send.`
                : `Sent to ${r.to}.`);
        } catch (ex) {
            setSendMsg(ex.response?.data?.detail || "Failed");
        } finally { setSending(false); }
    };

    return (
        <>
            <Field label="Trading date">
                <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="report-date" />
            </Field>

            {loading && <p className="text-muted mt-4">Crunching numbers…</p>}
            {err && <p className="text-[#ff5246] text-sm font-mono mt-4">{err}</p>}

            {report && !loading && (
                <div className="mt-5 space-y-4" data-testid="report-data">
                    <div className="grid grid-cols-3 gap-3">
                        <Kpi label="Orders" value={report.orders} />
                        <Kpi label="Revenue" value={`£${report.revenue.toFixed(2)}`} highlight />
                        <Kpi label="Avg basket" value={`£${report.average_basket.toFixed(2)}`} />
                    </div>

                    {report.top_items.length > 0 && (
                        <div>
                            <p className="eyebrow mb-2">// Top items</p>
                            <div className="border border-[var(--border)] divide-y divide-[var(--border)]">
                                {report.top_items.slice(0, 5).map((it, i) => (
                                    <div key={i} className="p-2.5 flex items-center justify-between text-sm">
                                        <span className="truncate">{i + 1}. {it.name}</span>
                                        <span className="font-mono text-muted">
                                            {it.qty}× · £{it.revenue.toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-3 pt-4 border-t border-[var(--border)]">
                        <button onClick={downloadPdf} className="btn-primary" data-testid="report-download">
                            ⬇ Download PDF
                        </button>
                    </div>

                    <div className="border-t border-[var(--border)] pt-4">
                        <p className="eyebrow mb-3">// Email to brand</p>
                        <div className="flex gap-2">
                            <input
                                className="input flex-1"
                                type="email"
                                placeholder="owner@brand.com"
                                value={emailTo}
                                onChange={(e) => setEmailTo(e.target.value)}
                                data-testid="report-email-to"
                            />
                            <button onClick={sendEmail} disabled={sending} className="btn-secondary whitespace-nowrap" data-testid="report-email-send">
                                {sending ? "…" : "Send"}
                            </button>
                        </div>
                        {sendMsg && (
                            <p className="font-mono text-[11px] text-muted mt-2" data-testid="report-email-status">{sendMsg}</p>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

const Row = ({ label, val, muted, strong, gold }) => (
    <div className={`flex justify-between ${gold ? "text-gold text-[14px]" : ""} ${strong ? "font-bold" : ""} ${muted ? "text-muted" : ""}`}>
        <span>{label}</span>
        <span>{val}</span>
    </div>
);

const Kpi = ({ label, value, highlight, testid }) => (
    <div data-testid={testid} className={`bg-[var(--surface-elevated)] border ${highlight ? "border-gold" : "border-[var(--border)]"} p-3`}>
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-muted mb-1">{label}</div>
        <div className={`font-display text-[24px] leading-none ${highlight ? "text-gold" : ""}`}>{value}</div>
    </div>
);

// ===== Modifier Groups Editor =====
const newId = () => Math.random().toString(36).slice(2, 10);

const ModifierGroupsEditor = ({ groups, onChange, brand, hideAdd }) => {
    const addGroup = (preset) => {
        const presets = {
            size: { id: newId(), name: "Choose size", type: "single_required",
                options: [
                    { id: newId(), name: "Small", price_delta: 0 },
                    { id: newId(), name: "Medium", price_delta: 2 },
                    { id: newId(), name: "Large", price_delta: 4 },
                ]},
            toppings: { id: newId(), name: "Add toppings", type: "multi", max_select: null,
                options: [
                    { id: newId(), name: "Extra cheese", price_delta: 1.5 },
                    { id: newId(), name: "Bacon", price_delta: 2 },
                ]},
            dining: { id: newId(), name: "Dine in or take away", type: "single_required",
                options: [
                    { id: newId(), name: "Dine in", price_delta: 0 },
                    { id: newId(), name: "Take away", price_delta: 0 },
                ]},
            meal: { id: newId(), name: "Make it a meal?", type: "single_optional",
                options: [
                    { id: newId(), name: "No thanks", description: "Just the item", price_delta: 0 },
                    { id: newId(), name: "Make it a meal", description: "Adds regular fries + a soft drink", price_delta: 3.5 },
                    { id: newId(), name: "Make it a large meal", description: "Adds large fries + large soft drink", price_delta: 5 },
                ]},
            blank: { id: newId(), name: "New options group", type: "single_required", options: [] },
        };
        onChange([...(groups || []), presets[preset] || presets.blank]);
    };

    const update = (gi, patch) => onChange(groups.map((g, i) => i === gi ? { ...g, ...patch } : g));
    const remove = (gi) => onChange(groups.filter((_, i) => i !== gi));
    const addOpt = (gi) => update(gi, { options: [...(groups[gi].options || []), { id: newId(), name: "", price_delta: 0 }] });
    const updateOpt = (gi, oi, patch) => update(gi, { options: groups[gi].options.map((o, i) => i === oi ? { ...o, ...patch } : o) });
    const removeOpt = (gi, oi) => update(gi, { options: groups[gi].options.filter((_, i) => i !== oi) });

    // ----- Nested sub-modifiers (one level deep) -----
    // e.g. option "Chicken curry" → sub picker: Mild / Medium / Hot.
    const addSubGroup = (gi, oi) => updateOpt(gi, oi, {
        sub_group: {
            id: newId(),
            name: `${groups[gi].options[oi].name || "Option"} — pick one`,
            type: "single_required",
            options: [
                { id: newId(), name: "", price_delta: 0 },
            ],
        },
    });
    const patchSubGroup = (gi, oi, patch) => updateOpt(gi, oi, {
        sub_group: { ...(groups[gi].options[oi].sub_group || {}), ...patch },
    });
    const removeSubGroup = (gi, oi) => updateOpt(gi, oi, { sub_group: null });
    const addSubOpt = (gi, oi) => {
        const sg = groups[gi].options[oi].sub_group;
        patchSubGroup(gi, oi, { options: [...(sg.options || []), { id: newId(), name: "", price_delta: 0 }] });
    };
    const updateSubOpt = (gi, oi, si, patch) => {
        const sg = groups[gi].options[oi].sub_group;
        patchSubGroup(gi, oi, { options: sg.options.map((s, i) => i === si ? { ...s, ...patch } : s) });
    };
    const removeSubOpt = (gi, oi, si) => {
        const sg = groups[gi].options[oi].sub_group;
        patchSubGroup(gi, oi, { options: sg.options.filter((_, i) => i !== si) });
    };

    return (
        <div className="border-t border-[var(--border)] pt-4" data-testid="modifier-groups-editor">
            <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                    Options / Modifiers
                </span>
                <div className={`flex flex-wrap gap-1 ${hideAdd ? "hidden" : ""}`}>
                    <button type="button" onClick={() => addGroup("size")} className="font-mono text-[9px] uppercase tracking-[0.18em] border border-[var(--border)] hover:border-[var(--gold)] px-2 py-1" data-testid="add-size-group">+ Size</button>
                    <button type="button" onClick={() => addGroup("toppings")} className="font-mono text-[9px] uppercase tracking-[0.18em] border border-[var(--border)] hover:border-[var(--gold)] px-2 py-1" data-testid="add-toppings-group">+ Toppings</button>
                    <button type="button" onClick={() => addGroup("dining")} className="font-mono text-[9px] uppercase tracking-[0.18em] border border-[var(--border)] hover:border-[var(--gold)] px-2 py-1" data-testid="add-dining-group">+ Dine/Take away</button>
                    <button type="button" onClick={() => addGroup("meal")} className="font-mono text-[9px] uppercase tracking-[0.18em] border border-[var(--border)] hover:border-[var(--gold)] px-2 py-1" data-testid="add-meal-group">+ Make it a meal</button>
                    <button type="button" onClick={() => addGroup("blank")} className="font-mono text-[9px] uppercase tracking-[0.18em] border border-[var(--gold)] text-gold px-2 py-1" data-testid="add-blank-group">+ Custom</button>
                </div>
            </div>

            {(!groups || groups.length === 0) && (
                <p className="text-muted text-[12px] italic">No options yet — tap one of the presets above.</p>
            )}

            {(groups || []).map((g, gi) => (
                <div key={g.id} className={`border p-3 mb-3 ${g.library_id ? "border-[var(--gold)]" : "border-[var(--border)]"}`} data-testid={`mod-group-${gi}`}>
                    {brand && (
                        <SaveGroupControl
                            brand={brand}
                            group={g}
                            onLinked={(libId) => update(gi, { library_id: libId })}
                            onUnlink={() => update(gi, { library_id: null })}
                        />
                    )}
                    <div className="flex gap-2 mb-2">
                        <input
                            className="input flex-1 text-sm"
                            value={g.name}
                            onChange={(e) => update(gi, { name: e.target.value })}
                            placeholder="Group name (e.g. Choose size)"
                            data-testid={`mod-group-name-${gi}`}
                        />
                        <select
                            className="input !w-44 text-sm"
                            value={g.type}
                            onChange={(e) => update(gi, { type: e.target.value })}
                            data-testid={`mod-group-type-${gi}`}
                        >
                            <option value="single_required">Pick one (required)</option>
                            <option value="single_optional">Pick one (optional)</option>
                            <option value="multi">Pick multi (min / max)</option>
                        </select>
                        <button type="button" onClick={() => remove(gi)} className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff5246] px-2">✕</button>
                    </div>
                    {g.type === "multi" && (
                        <div className="flex gap-3 items-center pl-3 mb-2" data-testid={`mod-group-multi-bounds-${gi}`}>
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted flex items-center gap-1">
                                Min
                                <input
                                    className="input !w-16 text-sm h-8"
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={g.min_select ?? ""}
                                    onChange={(e) => update(gi, { min_select: e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0) })}
                                    placeholder="0"
                                    data-testid={`mod-group-min-${gi}`}
                                />
                            </label>
                            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted flex items-center gap-1">
                                Max
                                <input
                                    className="input !w-16 text-sm h-8"
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={g.max_select ?? ""}
                                    onChange={(e) => update(gi, { max_select: e.target.value === "" ? null : Math.max(1, parseInt(e.target.value, 10) || 1) })}
                                    placeholder="—"
                                    data-testid={`mod-group-max-${gi}`}
                                />
                            </label>
                            <span className="font-mono text-[10px] text-muted">
                                {g.min_select > 0 ? `Customer must pick ≥ ${g.min_select}` : "Optional"}
                                {g.max_select ? ` · up to ${g.max_select}` : " · unlimited"}
                            </span>
                        </div>
                    )}
                    <div className="space-y-2 pl-3 border-l border-[var(--border)]">
                        {(g.options || []).map((opt, oi) => (
                            <div key={opt.id} className="space-y-1" data-testid={`mod-opt-${gi}-${oi}`}>
                                <div className="flex gap-2 items-center">
                                    <input
                                        className="input flex-1 text-sm h-9"
                                        value={opt.name}
                                        onChange={(e) => updateOpt(gi, oi, { name: e.target.value })}
                                        placeholder="Option name (e.g. Make it a meal)"
                                    />
                                    <div className="flex items-center gap-1">
                                        <span className="font-mono text-[11px] text-muted">£</span>
                                        <input
                                            className="input !w-20 text-sm h-9"
                                            type="number"
                                            step="0.5"
                                            value={opt.price_delta}
                                            onChange={(e) => updateOpt(gi, oi, { price_delta: parseFloat(e.target.value) || 0 })}
                                            placeholder="+0"
                                        />
                                    </div>
                                    <button type="button" onClick={() => removeOpt(gi, oi)} className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-[#ff5246]">✕</button>
                                </div>
                                <input
                                    className="input w-full text-[12px] h-8 ml-0"
                                    value={opt.description || ""}
                                    onChange={(e) => updateOpt(gi, oi, { description: e.target.value })}
                                    placeholder="Short description (optional) — e.g. Add fries + drink"
                                />
                                {/* Nested sub-modifier: fires when THIS option is picked
                                    (e.g. Chicken curry → Mild / Medium / Hot). */}
                                {!opt.sub_group ? (
                                    <button
                                        type="button"
                                        onClick={() => addSubGroup(gi, oi)}
                                        className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-gold"
                                        data-testid={`add-sub-group-${gi}-${oi}`}
                                    >
                                        + Add follow-up choice
                                    </button>
                                ) : (
                                    <div
                                        className="mt-2 border border-[var(--gold)]/40 bg-[var(--gold)]/5 p-2 space-y-2"
                                        data-testid={`sub-group-${gi}-${oi}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-gold whitespace-nowrap">
                                                ↳ Follow-up
                                            </span>
                                            <input
                                                className="input flex-1 text-sm h-8"
                                                value={opt.sub_group.name || ""}
                                                onChange={(e) => patchSubGroup(gi, oi, { name: e.target.value })}
                                                placeholder="Sub picker label (e.g. Spice level)"
                                                data-testid={`sub-group-name-${gi}-${oi}`}
                                            />
                                            <select
                                                className="input !w-40 !h-9 !py-1 !text-[12px]"
                                                value={opt.sub_group.type}
                                                onChange={(e) => patchSubGroup(gi, oi, { type: e.target.value })}
                                                data-testid={`sub-group-type-${gi}-${oi}`}
                                            >
                                                <option value="single_required">Pick one (required)</option>
                                                <option value="single_optional">Pick one (optional)</option>
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => removeSubGroup(gi, oi)}
                                                className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff5246]"
                                                data-testid={`remove-sub-group-${gi}-${oi}`}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                        <div className="space-y-1 pl-3 border-l border-[var(--gold)]/30">
                                            {(opt.sub_group.options || []).map((so, si) => (
                                                <div key={so.id} className="flex gap-2 items-center" data-testid={`sub-opt-${gi}-${oi}-${si}`}>
                                                    <input
                                                        className="input flex-1 text-sm h-8"
                                                        value={so.name}
                                                        onChange={(e) => updateSubOpt(gi, oi, si, { name: e.target.value })}
                                                        placeholder="Sub-option (e.g. Mild)"
                                                    />
                                                    <span className="font-mono text-[11px] text-muted">£</span>
                                                    <input
                                                        className="input !w-20 text-sm h-8"
                                                        type="number"
                                                        step="0.5"
                                                        value={so.price_delta}
                                                        onChange={(e) => updateSubOpt(gi, oi, si, { price_delta: parseFloat(e.target.value) || 0 })}
                                                        placeholder="+0"
                                                    />
                                                    <button type="button" onClick={() => removeSubOpt(gi, oi, si)} className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-[#ff5246]">✕</button>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => addSubOpt(gi, oi)}
                                                className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold hover:underline"
                                                data-testid={`add-sub-opt-${gi}-${oi}`}
                                            >
                                                + Add sub-option
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={() => addOpt(gi)} className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold hover:underline mt-1">
                            + Add option
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default AdminBrandEditor;
